import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, Settings, Trash2, UserPlus, X } from 'lucide-react';
import { doc, getDoc, collection, getDocs, query, orderBy, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import BBALoader from '../components/BBALoader';
import {
  GLASS_TH,
  GLASS_ROW,
  GLASS_INSET,
  GLASS_MODAL_OVERLAY,
  GLASS_MODAL_PANEL,
  GlassPage,
  GlassPageHeader,
  GlassCard,
  GlassCardHeader,
  GlassCircleButton,
  GlassBadge,
  GlassAvatar,
  GlassAccentButton,
  GlassEmpty,
} from '../components/glass/GlassUi';

const BACK_TO = '/crm/conferences-reports';

const DANGER_BUTTON = 'inline-flex items-center gap-2 rounded-full border border-[#F80707]/30 bg-[#F80707]/[0.06] px-4 py-2 text-sm font-medium text-[#E00606] transition hover:bg-[#F80707]/10 disabled:cursor-not-allowed disabled:opacity-40 dark:border-[#F80707]/40 dark:bg-[#F80707]/10 dark:text-[#FF4D40] dark:hover:bg-[#F80707]/20';

const ConferenceOverview = () => {
  const { conferenceId } = useParams();
  const navigate = useNavigate();

  const [conference, setConference] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isStarred, setIsStarred] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchConferenceData = async () => {
      try {
        // Fetch conference data
        const conferenceRef = doc(db, 'conferences', conferenceId);
        const conferenceSnap = await getDoc(conferenceRef);

        if (!conferenceSnap.exists()) {
          console.error('Conference not found');
          setLoading(false);
          return;
        }

        const conferenceData = {
          id: conferenceSnap.id,
          ...conferenceSnap.data(),
          startDate: conferenceSnap.data().startDate?.toDate() || null,
          endDate: conferenceSnap.data().endDate?.toDate() || null,
          date: conferenceSnap.data().date?.toDate() || conferenceSnap.data().startDate?.toDate() || null
        };
        setConference(conferenceData);
        setIsStarred(conferenceSnap.data().starred || false);

        // Fetch leads from subcollection
        const leadsRef = collection(db, 'conferences', conferenceId, 'leads');
        const leadsQuery = query(leadsRef, orderBy('date', 'desc'));
        const leadsSnapshot = await getDocs(leadsQuery);

        const leadsData = leadsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate() || doc.data().createdAt?.toDate() || null
        }));

        setLeads(leadsData);
      } catch (error) {
        console.error('Error fetching conference data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (conferenceId) {
      fetchConferenceData();
    }
  }, [conferenceId]);

  const handleStarClick = async () => {
    try {
      const newStarredState = !isStarred;
      setIsStarred(newStarredState);

      // Update in Firestore
      const conferenceRef = doc(db, 'conferences', conferenceId);
      await updateDoc(conferenceRef, {
        starred: newStarredState
      });
    } catch (error) {
      console.error('Error updating star status:', error);
      // Revert state on error
      setIsStarred(!isStarred);
    }
  };

  const handleRowClick = (lead) => {
    setSelectedLead(lead);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedLead(null);
  };

  const handleDeleteLead = async () => {
    if (!selectedLead || !selectedLead.id) return;

    if (!window.confirm('Are you sure you want to delete this lead? This action cannot be undone.')) {
      return;
    }

    setIsDeleting(true);
    try {
      const leadRef = doc(db, 'conferences', conferenceId, 'leads', selectedLead.id);
      await deleteDoc(leadRef);

      // Update local state
      setLeads(leads.filter(lead => lead.id !== selectedLead.id));
      handleCloseModal();
    } catch (error) {
      console.error('Error deleting lead:', error);
      alert('Failed to delete lead. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMakeLead = async () => {
    // TODO: Implement make lead functionality
    console.log('Make lead clicked for:', selectedLead);
    alert('Make Lead functionality to be implemented');
  };

  if (loading) {
    return (
      <GlassPage>
        <GlassPageHeader backTo={BACK_TO} backTitle="Back to conferences" title={null} />
        <div className="mt-8 text-center">
          <BBALoader size={48} className="mx-auto mb-4" />
          <p className="mt-4 text-gray-600 dark:text-white/60">Loading conference...</p>
        </div>
      </GlassPage>
    );
  }

  if (!conference) {
    return (
      <GlassPage>
        <GlassPageHeader backTo={BACK_TO} backTitle="Back to conferences" title={null} />
        <GlassCard className="p-6">
          <GlassEmpty className="h-32">Conference not found</GlassEmpty>
        </GlassCard>
      </GlassPage>
    );
  }

  const tdCls = 'whitespace-nowrap py-3 pr-4';
  const fieldLabel = 'mb-1 block text-xs font-medium text-gray-500 dark:text-white/[0.45]';
  const fieldValue = 'text-sm text-gray-900 dark:text-white';

  return (
    <GlassPage>
      <GlassPageHeader
        backTo={BACK_TO}
        backTitle="Back to conferences"
        title={conference.name}
        controls={(
          <>
            <GlassCircleButton
              onClick={() => navigate(`/crm/conferences-reports/${conferenceId}/spinwheel`)}
              title="Open spin wheel"
            >
              <Settings className="h-[18px] w-[18px]" strokeWidth={1.75} />
            </GlassCircleButton>
            <GlassCircleButton
              onClick={handleStarClick}
              title={isStarred ? 'Unstar conference' : 'Star conference'}
            >
              <Star
                className={`h-[18px] w-[18px] transition-colors ${isStarred ? 'fill-[#FFB547] text-[#FFB547]' : ''}`}
                strokeWidth={1.75}
              />
            </GlassCircleButton>
          </>
        )}
      />

      {/* Leads Table */}
      <GlassCard className="p-6" delay={0}>
        <GlassCardHeader
          title="Leads"
          right={<span className="text-xs text-gray-500 dark:text-white/50">{leads.length} leads</span>}
        />
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr>
                <th className={GLASS_TH}>First Name</th>
                <th className={GLASS_TH}>Last Name</th>
                <th className={GLASS_TH}>Email</th>
                <th className={GLASS_TH}>Phone</th>
                <th className={GLASS_TH}>Company Name</th>
                <th className={GLASS_TH}>Date</th>
                <th className={GLASS_TH}>Booked</th>
              </tr>
            </thead>
            <tbody className="text-gray-700 dark:text-white/75">
              {leads.length === 0 ? (
                <tr>
                  <td colSpan="7" className="py-8 text-center text-sm text-gray-500 dark:text-white/50">
                    No leads found for this conference
                  </td>
                </tr>
              ) : (
                leads.map((lead) => {
                  const isBooked = lead.booked || lead.isBooked || lead.bookedStatus === true || lead.bookedStatus === 'yes' || lead.bookedStatus === 'Yes';
                  const firstName = lead.firstName || lead.first_name || '-';
                  const lastName = lead.lastName || lead.last_name || '-';
                  return (
                    <tr
                      key={lead.id}
                      onClick={() => handleRowClick(lead)}
                      className={`${GLASS_ROW} cursor-pointer`}
                    >
                      <td className={tdCls}>
                        <div className="flex items-center gap-3">
                          <GlassAvatar name={`${firstName === '-' ? '' : firstName} ${lastName === '-' ? '' : lastName}`} />
                          <span className="font-medium text-gray-900 dark:text-white">{firstName}</span>
                        </div>
                      </td>
                      <td className={`${tdCls} font-medium text-gray-900 dark:text-white`}>
                        {lastName}
                      </td>
                      <td className={`${tdCls} text-gray-500 dark:text-white/50`}>
                        {lead.email || '-'}
                      </td>
                      <td className={`${tdCls} text-gray-500 dark:text-white/50`}>
                        {lead.phone || lead.phoneNumber || '-'}
                      </td>
                      <td className={tdCls}>
                        {lead.companyName || lead.company || '-'}
                      </td>
                      <td className={`${tdCls} tabular-nums`}>
                        {lead.date ? lead.date.toLocaleDateString() : '-'}
                      </td>
                      <td className="whitespace-nowrap py-3">
                        <GlassBadge tone={isBooked ? 'mint' : 'neutral'}>{isBooked ? 'Yes' : 'No'}</GlassBadge>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </GlassCard>

      {/* Lead Details Modal — kept outside the glass card so backdrop-filter can't trap it */}
      {isModalOpen && (
        <div className={GLASS_MODAL_OVERLAY} onClick={handleCloseModal}>
          <div className={GLASS_MODAL_PANEL} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={handleCloseModal}
              className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-full text-gray-400 transition hover:bg-black/[0.05] hover:text-[#F80707] dark:text-white/40 dark:hover:bg-white/[0.06] dark:hover:text-[#FF4D40]"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            {selectedLead && (
              <div>
                <div className="mb-6 flex items-center justify-center">
                  <h2 className="text-2xl font-medium tracking-tight text-gray-900 dark:text-white">
                    Lead Details
                  </h2>
                </div>

                <div className="space-y-4">
                  <div className={`${GLASS_INSET} grid grid-cols-2 gap-4 p-4`}>
                    <div>
                      <label className={fieldLabel}>
                        First Name
                      </label>
                      <p className={fieldValue}>
                        {selectedLead.firstName || selectedLead.first_name || '-'}
                      </p>
                    </div>
                    <div>
                      <label className={fieldLabel}>
                        Last Name
                      </label>
                      <p className={fieldValue}>
                        {selectedLead.lastName || selectedLead.last_name || '-'}
                      </p>
                    </div>
                    <div>
                      <label className={fieldLabel}>
                        Email
                      </label>
                      <p className={`${fieldValue} break-all`}>
                        {selectedLead.email || '-'}
                      </p>
                    </div>
                    <div>
                      <label className={fieldLabel}>
                        Phone
                      </label>
                      <p className={fieldValue}>
                        {selectedLead.phone || selectedLead.phoneNumber || '-'}
                      </p>
                    </div>
                    <div>
                      <label className={fieldLabel}>
                        Company Name
                      </label>
                      <p className={fieldValue}>
                        {selectedLead.companyName || selectedLead.company || '-'}
                      </p>
                    </div>
                    <div>
                      <label className={fieldLabel}>
                        Date
                      </label>
                      <p className={fieldValue}>
                        {selectedLead.date ? selectedLead.date.toLocaleDateString() : '-'}
                      </p>
                    </div>
                  </div>

                  {/* Additional Information section - under Company Name */}
                  <div className="mt-4 border-t border-black/[0.06] pt-4 dark:border-white/[0.08]">
                    <h3 className="mb-4 flex items-center gap-2.5 text-[15px] font-medium text-gray-900 dark:text-white/90">
                      <span className="h-4 w-1 rounded-full bg-[#F80707]" />
                      Additional Information
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between gap-4">
                        <span className="text-sm font-medium text-gray-500 dark:text-white/[0.45]">
                          Booked:
                        </span>
                        <span className="text-sm text-gray-900 dark:text-white">
                          {(selectedLead.booked || selectedLead.isBooked || selectedLead.bookedStatus === true || selectedLead.bookedStatus === 'yes' || selectedLead.bookedStatus === 'Yes') ? 'Yes' : 'No'}
                        </span>
                      </div>
                      {Object.entries(selectedLead)
                        .filter(([key]) =>
                          !['id', 'firstName', 'first_name', 'lastName', 'last_name', 'email', 'phone', 'phoneNumber', 'companyName', 'company', 'date', 'booked', 'isBooked', 'bookedStatus', 'createdAt'].includes(key)
                        )
                        .map(([key, value]) => (
                          <div key={key} className="flex justify-between gap-4">
                            <span className="text-sm font-medium capitalize text-gray-500 dark:text-white/[0.45]">
                              {key.replace(/([A-Z])/g, ' $1').trim()}:
                            </span>
                            <span className="text-right text-sm text-gray-900 dark:text-white">
                              {value instanceof Date ? value.toLocaleDateString() : String(value || '-')}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>

                  <div className="mt-6 flex justify-between border-t border-black/[0.06] pt-6 dark:border-white/[0.08]">
                    <button
                      type="button"
                      onClick={handleDeleteLead}
                      disabled={isDeleting}
                      className={DANGER_BUTTON}
                    >
                      <Trash2 className="h-4 w-4" />
                      {isDeleting ? 'Deleting...' : 'Delete Lead'}
                    </button>
                    <GlassAccentButton onClick={handleMakeLead}>
                      <UserPlus className="h-4 w-4" />
                      Make Lead
                    </GlassAccentButton>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </GlassPage>
  );
};

export default ConferenceOverview;

